import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { CardElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import '../styles/DonationModal.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null;

// ISO 3166-1 alpha-2 → ISO 4217 currency
const COUNTRY_CURRENCY = {
  AF:'AFN', AL:'ALL', DZ:'DZD', AD:'EUR', AO:'AOA', AG:'XCD', AR:'ARS',
  AM:'AMD', AU:'AUD', AT:'EUR', AZ:'AZN', BS:'BSD', BH:'BHD', BD:'BDT',
  BB:'BBD', BY:'BYN', BE:'EUR', BZ:'BZD', BJ:'XOF', BT:'BTN', BO:'BOB',
  BA:'BAM', BW:'BWP', BR:'BRL', BN:'BND', BG:'BGN', BF:'XOF', BI:'BIF',
  CV:'CVE', KH:'KHR', CM:'XAF', CA:'CAD', CF:'XAF', TD:'XAF', CL:'CLP',
  CN:'CNY', CO:'COP', KM:'KMF', CG:'XAF', CD:'CDF', CR:'CRC', CI:'XOF',
  HR:'EUR', CY:'EUR', CZ:'CZK', DK:'DKK', DJ:'DJF', DM:'XCD', DO:'DOP',
  EC:'USD', EG:'EGP', SV:'USD', GQ:'XAF', ER:'ERN', EE:'EUR', SZ:'SZL',
  ET:'ETB', FJ:'FJD', FI:'EUR', FR:'EUR', GA:'XAF', GM:'GMD', GE:'GEL',
  DE:'EUR', GH:'GHS', GR:'EUR', GD:'XCD', GT:'GTQ', GN:'GNF', GW:'XOF',
  GY:'GYD', HT:'HTG', HN:'HNL', HU:'HUF', IS:'ISK', IN:'INR', ID:'IDR',
  IQ:'IQD', IE:'EUR', IL:'ILS', IT:'EUR', JM:'JMD', JP:'JPY', JO:'JOD',
  KZ:'KZT', KE:'KES', KI:'AUD', KW:'KWD', KG:'KGS', LA:'LAK', LV:'EUR',
  LB:'LBP', LS:'LSL', LR:'LRD', LY:'LYD', LI:'CHF', LT:'EUR', LU:'EUR',
  MG:'MGA', MW:'MWK', MY:'MYR', MV:'MVR', ML:'XOF', MT:'EUR', MH:'USD',
  MR:'MRU', MU:'MUR', MX:'MXN', FM:'USD', MD:'MDL', MC:'EUR', MN:'MNT',
  ME:'EUR', MA:'MAD', MZ:'MZN', MM:'MMK', NA:'NAD', NR:'AUD', NP:'NPR',
  NL:'EUR', NZ:'NZD', NI:'NIO', NE:'XOF', NG:'NGN', NO:'NOK', OM:'OMR',
  PK:'PKR', PW:'USD', PA:'PAB', PG:'PGK', PY:'PYG', PE:'PEN', PH:'PHP',
  PL:'PLN', PT:'EUR', QA:'QAR', RO:'RON', RU:'RUB', RW:'RWF', KN:'XCD',
  LC:'XCD', VC:'XCD', WS:'WST', SM:'EUR', ST:'STN', SA:'SAR', SN:'XOF',
  RS:'RSD', SC:'SCR', SL:'SLL', SG:'SGD', SK:'EUR', SI:'EUR', SB:'SBD',
  SO:'SOS', ZA:'ZAR', SS:'SSP', ES:'EUR', LK:'LKR', SR:'SRD', SE:'SEK',
  CH:'CHF', TW:'TWD', TJ:'TJS', TZ:'TZS', TH:'THB', TL:'USD', TG:'XOF',
  TO:'TOP', TT:'TTD', TN:'TND', TR:'TRY', TM:'TMT', UG:'UGX', UA:'UAH',
  AE:'AED', GB:'GBP', US:'USD', UY:'UYU', UZ:'UZS', VU:'VUV', VN:'VND',
  YE:'YER', ZM:'ZMW', ZW:'ZWL',
};

// Stripe zero-decimal currencies — no multiplication by 100
const ZERO_DECIMAL = new Set([
  'BIF','CLP','DJF','GNF','JPY','KMF','KRW','MGA','PYG','RWF',
  'UGX','VND','VUV','XAF','XOF','XPF',
]);

function formatMoney(amount, currency) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: ZERO_DECIMAL.has(currency) ? 0 : 2,
      maximumFractionDigits: ZERO_DECIMAL.has(currency) ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
}

function DonationContent({ user, onClose }) {
  const [method, setMethod]           = useState('stripe');
  const [usdAmount, setUsdAmount]     = useState('10');
  const [currency, setCurrency]       = useState('USD');
  const [rate, setRate]               = useState(1);
  const [rateLoading, setRateLoading] = useState(false);
  const [phone, setPhone]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [feedback, setFeedback]       = useState(null); // {type:'success'|'error', text:''}
  const stripe   = useStripe();
  const elements = useElements();

  // Mobile money needs its own country/currency picker — a payer's profile
  // country was previously the *only* source of currency for PawaPay, with
  // no way to override it (e.g. a DRC user forced into CDF even if they
  // only hold USD). Fetched live from PawaPay rather than a fixed list.
  const [momoCountries, setMomoCountries] = useState([]);
  const [momoCountry, setMomoCountry]     = useState('');

  useEffect(() => {
    axios.get(`${API_BASE}/pawapay/providers`)
      .then(({ data }) => setMomoCountries(data.countries || []))
      .catch(() => {});
  }, []);

  const momoCurrencyOptions = (() => {
    const country = momoCountries.find(c => c.country === momoCountry);
    if (!country) return [];
    const seen = new Map();
    for (const provider of country.providers || []) {
      for (const curr of provider.currencies || []) {
        if (curr.operationTypes?.DEPOSIT?.status !== 'OPERATIONAL') continue;
        if (!seen.has(curr.currency)) seen.set(curr.currency, curr.displayName || curr.currency);
      }
    }
    return Array.from(seen, ([code, label]) => ({ code, label }));
  })();

  const handleMomoCountryChange = (countryCode) => {
    setMomoCountry(countryCode);
    setCurrency('USD'); // force a real re-selection rather than keeping a stale currency
  };

  const handleMomoCurrencyChange = (code) => {
    setCurrency(code);
    if (code === 'USD') setRate(1); else fetchRate(code);
  };

  const showFeedback = (type, text) => {
    setFeedback({ type, text });
    if (type === 'success') setTimeout(onClose, 2000);
  };

  const USD_PRESETS = [5, 10, 25, 50, 100];

  const localAmount = ZERO_DECIMAL.has(currency)
    ? Math.round(parseFloat(usdAmount || 0) * rate)
    : Math.round(parseFloat(usdAmount || 0) * rate * 100) / 100;

  // Auto-detect currency from user's country on mount
  useEffect(() => {
    const code = user?.country_residence?.toUpperCase();
    const detected = (code && COUNTRY_CURRENCY[code]) || 'USD';
    setCurrency(detected);
    if (detected !== 'USD') fetchRate(detected);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRate = async (target) => {
    setRateLoading(true);
    try {
      const res  = await fetch(
        'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json'
      );
      const data = await res.json();
      const r    = data?.usd?.[target.toLowerCase()];
      if (r) setRate(r);
    } catch {
      // Keep rate = 1 on network failure
    } finally {
      setRateLoading(false);
    }
  };

  const handleStripePayment = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setFeedback(null);
    try {
      const { data } = await axios.post(`${API_BASE}/donations/create-stripe-intent`, {
        amount:     localAmount,
        currency,
        user_email: 'anonymous@proteste.io',
        user_id:    user?.id ?? null,
      });

      if (!data.success) {
        showFeedback('error', 'Could not create payment: ' + data.error);
        return;
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(data.client_secret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (error) {
        showFeedback('error', error.message);
      } else if (paymentIntent?.status === 'succeeded') {
        await axios.post(`${API_BASE}/donations/confirm-stripe`, {
          payment_id: paymentIntent.id,
        }).catch(() => {});
        showFeedback('success', 'Thank you for your donation!');
      } else {
        showFeedback('error', 'Payment incomplete. Please try again.');
      }
    } catch (err) {
      showFeedback('error', err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMobilePayment = async (e) => {
    e.preventDefault();
    if (!phone) { showFeedback('error', 'Please enter your phone number.'); return; }
    setLoading(true);
    setFeedback(null);
    try {
      const { data } = await axios.post(`${API_BASE}/donations/mobile-money`, {
        amount:       localAmount,
        phone_number: phone,
        currency,
        user_id:      user?.id ?? null,
      });
      if (data.success) {
        showFeedback('success', 'Donation initiated! Check your phone for confirmation.');
      } else {
        showFeedback('error', data.error);
      }
    } catch (err) {
      showFeedback('error', err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="donation-content">
      <h2>Support Proteste's Mission</h2>
      <p className="donation-intro">
        Your donation helps amplify unheard voices around the world.
        Thank you for believing in democratic voice.
      </p>

      <div className="method-selector">
        <button className={`method-btn ${method === 'stripe'  ? 'active' : ''}`} onClick={() => setMethod('stripe')}>
          Card Payment (Stripe)
        </button>
        <button className={`method-btn ${method === 'pawapay' ? 'active' : ''}`} onClick={() => setMethod('pawapay')}>
          Mobile Money (Africa)
        </button>
      </div>

      {/* Amount selector — always in USD, shows local equivalent */}
      <div className="amount-selector">
        <label>Amount (USD)</label>
        <div className="quick-amounts">
          {USD_PRESETS.map((amt) => (
            <button
              key={amt}
              className={`amount-btn ${usdAmount === String(amt) ? 'active' : ''}`}
              onClick={() => setUsdAmount(String(amt))}
            >
              ${amt}
            </button>
          ))}
        </div>
        <div className="custom-amount">
          <span className="currency-prefix">$</span>
          <input
            type="number"
            min="1"
            step="0.01"
            value={usdAmount}
            onChange={(e) => setUsdAmount(e.target.value)}
            placeholder="Custom amount"
          />
        </div>

        {/* Live conversion display */}
        {currency !== 'USD' && (
          <div className="conversion-box">
            {rateLoading ? (
              <span className="rate-loading">Fetching exchange rate…</span>
            ) : (
              <>
                <span className="converted-total">
                  You will be charged <strong>{formatMoney(localAmount, currency)}</strong>
                </span>
                <span className="exchange-rate">
                  1 USD = {rate.toLocaleString('en-US', { maximumFractionDigits: 4 })} {currency}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {method === 'stripe' && !STRIPE_KEY && (
        <div className="payment-unavailable">
          Card payments are not configured in this environment.
          Please use Mobile Money or contact us at <strong>contact@proteste.io</strong>.
        </div>
      )}

      {method === 'stripe' && STRIPE_KEY && (
        <form onSubmit={handleStripePayment} className="payment-form">
          <div className="card-element">
            <label>Card Details</label>
            <CardElement options={{ style: { base: { fontSize: '15px' } } }} />
          </div>
          <button type="submit" disabled={!stripe || loading || rateLoading}>
            {loading ? 'Processing…' : `Donate ${formatMoney(localAmount, currency)}`}
          </button>
        </form>
      )}

      {method === 'pawapay' && (
        <form onSubmit={handleMobilePayment} className="payment-form">
          <div className="form-group">
            <label>Country</label>
            <select
              value={momoCountry}
              onChange={(e) => handleMomoCountryChange(e.target.value)}
              disabled={momoCountries.length === 0}
            >
              <option value="" disabled>
                {momoCountries.length === 0 ? 'Loading countries…' : 'Select your country'}
              </option>
              {momoCountries.map(c => (
                <option key={c.country} value={c.country}>
                  {c.displayName?.en || c.displayName?.fr || c.country}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Currency</label>
            <select
              value={momoCountry ? currency : ''}
              onChange={(e) => handleMomoCurrencyChange(e.target.value)}
              disabled={momoCurrencyOptions.length === 0}
            >
              <option value="" disabled>
                {momoCountry ? 'Select a currency' : 'Select a country first'}
              </option>
              {momoCurrencyOptions.map(c => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+256 … (Uganda), +260 … (Zambia)"
              required
            />
          </div>
          <p className="mobile-note">
            Supported networks: MTN, Airtel, M-Pesa, Orange, and more across Africa.
          </p>
          <button type="submit" disabled={loading || rateLoading || !momoCountry}>
            {loading ? 'Processing…' : `Donate ${formatMoney(localAmount, currency)}`}
          </button>
        </form>
      )}

      {feedback && (
        <div className={`donation-feedback donation-feedback--${feedback.type}`}>
          {feedback.text}
        </div>
      )}

      <div className="donation-info">
        <h4>Our Commitment</h4>
        <ul>
          <li>100% transparent — all funds go to operations</li>
          <li>No advertisements, no data selling</li>
          <li>Annual transparency reports published</li>
        </ul>
      </div>
    </div>
  );
}

function DonationModal({ user, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <Elements stripe={stripePromise}>
          <DonationContent user={user} onClose={onClose} />
        </Elements>
      </div>
    </div>
  );
}

export default DonationModal;
